import type { Schemas } from '@npc/api-client'
import type { VarlessCopyId } from '@npc/copy'
import { copyText } from '@npc/platform'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import type {
  CreateDevelopmentTaskInput,
  DevelopmentTask,
  DevelopmentTaskClaim,
  DevelopmentTaskExecutorIdentity,
  DevelopmentTaskEvent,
  DevelopmentTaskRepoOption,
  MaintainerOnboardingDuration,
} from '../../app/development-tasks'
import {
  createDevelopmentTaskMaintainerOnboarding,
  createDevelopmentTaskMagicLink,
  getDevelopmentTaskPreviewReceipt,
  getDevelopmentTaskReleaseReceipt,
  revokeDevelopmentTaskMaintainerCredential,
} from '../../app/development-tasks'
import { ApiFailure } from '../../app/errors'
import { getRemoteAISettings, LOCAL_EXECUTOR_PROVIDER } from '../../app/remote-ai'
import type { ProjectRepositoryCandidate } from '../../app/project-repositories'
import {
  isDevelopmentTaskFinal,
  useAccountMembership,
  useAIDeveloperSettings,
  useCancelDevelopmentTask,
  useClaimDevelopmentTask,
  useCreateDevelopmentTask,
  useDevelopmentTask,
  useDevelopmentTaskEvents,
  useDevelopmentTaskOptions,
  usePrepareDevelopmentTask,
  useLinkProjectRepository,
  useProjectRepositories,
  useProjectRepositoryCandidates,
  useAddDevelopmentTaskComment,
  useDevelopmentTaskComments,
} from '../../app/queries'
import { T } from '../../copy/text'
import { formatElapsed, stagesOf, type StageKey } from './stage-card'
import { Button } from '../../components/button'
import { Card, CardHint, CardTitle } from '../../components/card'
import { cn } from '../../components/cn'
import { Modal } from '../../components/modal'
import { Page, PageHeader } from '../../components/page'
import { ScreenLoading } from '../../components/state'
import { StatusDot, Tag, type TagTone } from '../../components/tag'
import { t } from '../../copy'
import { renderDevelopmentTaskMaintainerPrompt } from './maintainer-prompt'
import { DeliveryMissionFromPreviewLane } from './delivery-mission'
import { PreviewLanePanel } from './preview-lane'
import {
  taskNeedsRebase,
  taskNextActionOf,
  userStateCopy,
} from '../project/modules/task-inbox'

type WorkDomain = NonNullable<CreateDevelopmentTaskInput['work_domains']>[number]
type ContextItem = NonNullable<CreateDevelopmentTaskInput['context_items']>[number]
type ContextKind = ContextItem['kind']

const domains: readonly WorkDomain[] = [
  'product', 'design', 'frontend', 'backend', 'data', 'infra', 'qa', 'release',
]

const contextKinds: readonly ContextKind[] = [
  'product_doc', 'design', 'ui_route', 'code_path', 'api_contract',
  'test_plan', 'data_migration', 'deployment', 'asset', 'reference',
]

const domainCopy: Record<WorkDomain, VarlessCopyId> = {
  product: 'development_task.domain.product',
  design: 'development_task.domain.design',
  frontend: 'development_task.domain.frontend',
  backend: 'development_task.domain.backend',
  data: 'development_task.domain.data',
  infra: 'development_task.domain.infra',
  qa: 'development_task.domain.qa',
  release: 'development_task.domain.release',
}

const contextCopy: Record<ContextKind, VarlessCopyId> = {
  product_doc: 'development_task.context.product_doc',
  design: 'development_task.context.design',
  ui_route: 'development_task.context.ui_route',
  code_path: 'development_task.context.code_path',
  api_contract: 'development_task.context.api_contract',
  test_plan: 'development_task.context.test_plan',
  data_migration: 'development_task.context.data_migration',
  deployment: 'development_task.context.deployment',
  asset: 'development_task.context.asset',
  reference: 'development_task.context.reference',
}

const stateCopy: Record<string, VarlessCopyId> = {
  draft: 'development_task.state.draft',
  ready_to_claim: 'development_task.state.ready_to_claim',
  running: 'development_task.state.running',
  gating: 'development_task.state.gating',
  pending_approval: 'development_task.state.pending_approval',
  reviewing: 'development_task.state.reviewing',
  ci_running: 'development_task.state.ci_running',
  v2_handoff: 'development_task.state.v2_handoff',
  fix_requested: 'development_task.state.fix_requested',
  ready: 'development_task.state.ready',
  cancelled: 'development_task.state.cancelled',
  failed: 'development_task.state.failed',
}

const eventCopy: Record<string, VarlessCopyId> = {
  task_created: 'development_task.event.task_created',
  task_state_changed: 'development_task.event.task_state_changed',
  attempt_claimed: 'development_task.event.attempt_claimed',
  attempt_state_changed: 'development_task.event.attempt_state_changed',
  task_cancelled: 'development_task.event.task_cancelled',
  attempt_progress: 'development_task.event.attempt_progress',
}

export function DevelopmentTaskCreate({
  project,
  surface = 'paper',
}: {
  project: Schemas['Project']
  surface?: 'paper' | 'lulu'
}) {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const lulu = surface === 'lulu'

  return (
    <>
      {lulu ? (
        <button
          type="button"
          className="pj-btn-primary inline-flex h-9 items-center justify-center rounded-full px-4 text-[13px] font-semibold"
          onClick={() => setOpen(true)}
        >
          {t('development_task.create.action')}
        </button>
      ) : (
        <section className="mb-npc-grid-lg flex flex-col gap-npc-gap-lg border-y border-npc-border-softer py-npc-grid-lg lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="m-0 text-npc-card-title font-semibold text-npc-ink">{t('development_task.create.title')}</h2>
            <p className="mb-0 mt-npc-gap-xs max-w-[720px] text-npc-caption leading-[1.6] text-npc-ink-soft">
              {t('development_task.create.subtitle')}
            </p>
          </div>
          <Button variant="primary" className="shrink-0" onClick={() => setOpen(true)}>
            {t('development_task.create.action')}
          </Button>
        </section>
      )}

      <Modal
        open={open}
        onOpenChange={setOpen}
        title={t('development_task.create.action')}
        description={project.name}
        className="w-[min(720px,calc(100vw-32px))]"
      >
        <CreateTaskForm
          project={project}
          onCreated={(task) => {
            setOpen(false)
            void navigate({ to: '/development-tasks/$taskId', params: { taskId: task.id } })
          }}
        />
      </Modal>
    </>
  )
}

export function CreateTaskForm({ project, onCreated, initial }: { project: Schemas['Project']; onCreated: (task: DevelopmentTask) => void | Promise<void>; initial?: Partial<CreateDevelopmentTaskInput> }) {
  const settings = useAIDeveloperSettings()
  const options = useDevelopmentTaskOptions(project.id)
  const create = useCreateDevelopmentTask(project.id)
  const [title, setTitle] = useState(initial?.title ?? '')
  const [instructions, setInstructions] = useState(initial?.instructions ?? '')
  const [criteria, setCriteria] = useState(initial?.acceptance_criteria?.map(item => item.text) ?? [''])
  const [workDomains, setWorkDomains] = useState<WorkDomain[]>([])
  const [contexts, setContexts] = useState<ContextDraft[]>(initial?.context_items?.map(item => ({...item, digest: item.digest ?? ''})) ?? [])
  const [createdTask, setCreatedTask] = useState<DevelopmentTask>()
  const [finishing, setFinishing] = useState(false)
  const [finishError, setFinishError] = useState('')
  const [repo, setRepo] = useState('')
  const [remoteAIIdentityID, setRemoteAIIdentityID] = useState('')
  const remoteAI = useQuery({
    queryKey: ['remote-ai', project.id],
    queryFn: () => getRemoteAISettings(project.id),
  })

  const selectedDeveloper = settings.isSuccess ? settings.data.selected : null
  const advisoryDeveloper = settings.data?.selected ?? settings.data?.recommended
  const repos = useMemo(() => options.data?.repos ?? [], [options.data?.repos])
  const selectedRepo = repos.length > 1 ? repos.find((item) => item.name === repo) : repos[0]
  const requiredReady = title.trim() !== '' && instructions.trim() !== '' && criteria.some((item) => item.trim() !== '')
  const canCreate = requiredReady

  function updateCriterion(index: number, value: string) {
    setCriteria((current) => current.map((item, itemIndex) => itemIndex === index ? value : item))
  }

  function updateContext(index: number, patch: Partial<ContextDraft>) {
    setContexts((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item))
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!canCreate || finishing) return

    const contextItems = contexts
      .filter((item) => item.title.trim() !== '' && item.locator.trim() !== '')
      .map<ContextItem>((item) => ({
        kind: item.kind,
        title: item.title.trim(),
        locator: item.locator.trim(),
        ...(item.digest.trim() ? { digest: item.digest.trim() } : {}),
      }))
    const input: CreateDevelopmentTaskInput = {
      title: title.trim(),
      instructions: instructions.trim(),
      acceptance_criteria: criteria.filter((item) => item.trim()).map((text) => ({ text: text.trim(), source: 'user' })),
      ...(workDomains.length ? { work_domains: workDomains } : {}),
      ...(contextItems.length ? { context_items: contextItems } : {}),
      ...(repos.length > 1 && selectedRepo ? { repo: selectedRepo.name } : {}),
      ...(remoteAIIdentityID === 'auto'
        ? { auto_assign_executor: true }
        : remoteAIIdentityID ? { remote_ai_identity_id: remoteAIIdentityID } : {}),
      ...(selectedDeveloper
        ? {
            developer_model: selectedDeveloper.model,
            developer_model_source: 'user_default',
            developer_model_revision: selectedDeveloper.revision,
          }
        : {}),
    }

    try {
      setFinishing(true)
      setFinishError('')
      const task = createdTask ?? await create.mutateAsync(input)
      setCreatedTask(task)
      await onCreated(task)
    } catch (error) {
      setFinishError(error instanceof Error ? error.message : '这次没完成，请重试。')
    } finally {
      setFinishing(false)
    }
  }

  return (
    <form className="grid min-w-0 gap-npc-gap-lg" onSubmit={(event) => void submit(event)}>
      <Field label={t('development_task.field.title')}>
        <input required value={title} onChange={(event) => setTitle(event.target.value)} className={fieldClass} />
      </Field>
      <Field label={t('development_task.field.instructions')}>
        <textarea required value={instructions} onChange={(event) => setInstructions(event.target.value)} rows={5} className={cn(fieldClass, 'resize-y leading-[1.6]')} />
      </Field>
      <div className="min-w-0">
        <div className="mb-npc-gap-xs flex flex-wrap items-baseline justify-between gap-npc-gap-sm">
          <span className="text-npc-caption font-semibold text-npc-ink-medium">{t('development_task.field.acceptance')}</span>
          <span className="text-npc-meta text-npc-ink-softer">{t('development_task.field.acceptance_hint')}</span>
        </div>
        <div className="grid min-w-0 gap-npc-gap-sm">
          {criteria.map((criterion, index) => (
            <div key={index} className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] gap-npc-gap-sm">
              <textarea
                aria-label={`${t('development_task.field.acceptance')} ${index + 1}`}
                required={index === 0}
                rows={index === 0 ? 3 : 2}
                value={criterion}
                onChange={(event) => updateCriterion(index, event.target.value)}
                className={cn(fieldClass, 'min-h-[72px] resize-y leading-[1.6]')}
              />
              {criteria.length > 1 ? (
                <Button variant="ghost" size="sm" type="button" aria-label={t('development_task.action.remove')} onClick={() => setCriteria((current) => current.filter((_, itemIndex) => itemIndex !== index))}>
                  {t('development_task.action.remove')}
                </Button>
              ) : null}
            </div>
          ))}
          <Button variant="secondary" size="sm" type="button" className="justify-self-start" onClick={() => setCriteria((current) => [...current, ''])}>
            {t('development_task.action.add_acceptance')}
          </Button>
        </div>
      </div>

      <RepositoryOptions
        projectId={project.id}
        options={options}
        repos={repos}
        selectedRepo={repo}
        onSelect={setRepo}
      />

      <Field label="交给远程 AI">
        <select value={remoteAIIdentityID} onChange={(event) => setRemoteAIIdentityID(event.target.value)} className={fieldClass}>
          <option value="">{t('development_task.assignee.manual')}</option>
          <option value="auto">{t('development_task.assignee.auto')}</option>
          {remoteAI.data?.identities.filter((identity) => identity.status === 'active' && identity.provider !== LOCAL_EXECUTOR_PROVIDER).map((identity) => (
            <option key={identity.id} value={identity.id}>
              {identity.name} · {identity.provider}/{identity.model}
            </option>
          ))}
        </select>
      </Field>

      {create.error ? <ErrorNotice message={create.error.message} /> : null}
      {finishError ? <ErrorNotice message={finishError} /> : null}
      {!requiredReady ? <p className="m-0 text-npc-caption text-npc-amber-ink">{t('development_task.hint.draft')}</p> : null}
      <div className="flex flex-wrap justify-end gap-npc-gap-sm">
        <Button variant="primary" type="submit" disabled={!canCreate || create.isPending || finishing}>
          {create.isPending ? t('development_task.create.submitting') : t('development_task.create.submit')}
        </Button>
      </div>

      <details className="border-t border-npc-border-softer pt-npc-card">
        <summary className="cursor-pointer text-npc-body-sm font-semibold text-npc-ink">
          {t('development_task.section.optional')}
          <span className="ml-npc-gap-sm text-npc-caption font-normal text-npc-ink-softer">{t('development_task.optional.summary', { domains: workDomains.length, contexts: contexts.length })}</span>
        </summary>
        <div className="mt-npc-card grid min-w-0 gap-npc-card">
          <section>
            <h3 className="m-0 text-npc-body-sm font-semibold text-npc-ink">{t('development_task.section.domains')}</h3>
            <p className="mb-0 mt-npc-gap-xs text-npc-caption leading-[1.6] text-npc-ink-soft">{t('development_task.hint.domains')}</p>
            <div className="mt-npc-gap-sm flex flex-wrap gap-npc-gap-sm">
              {domains.map((domain) => {
                const selected = workDomains.includes(domain)
                return <label key={domain} className={cn('inline-flex cursor-pointer items-center gap-npc-gap-xs rounded-npc-tag-sm border px-npc-tag-x py-npc-tag-y text-npc-caption', selected ? 'border-npc-green bg-npc-green-bg text-npc-green-deep' : 'border-npc-border bg-npc-panel text-npc-ink-medium')}><input type="checkbox" checked={selected} onChange={() => setWorkDomains((current) => selected ? current.filter((item) => item !== domain) : [...current, domain])} className="size-4 accent-npc-green" />{t(domainCopy[domain])}</label>
              })}
            </div>
          </section>
          <section className="border-t border-npc-border-softer pt-npc-card">
            <h3 className="m-0 text-npc-body-sm font-semibold text-npc-ink">{t('development_task.section.context')}</h3>
            <p className="mb-0 mt-npc-gap-xs text-npc-caption leading-[1.6] text-npc-ink-soft">{t('development_task.hint.context')}</p>
            <div className="mt-npc-gap-lg grid min-w-0 gap-npc-gap-lg">
              {contexts.map((context, index) => <div key={index} className="grid min-w-0 gap-npc-gap-sm border-l-2 border-npc-border pl-npc-gap-lg"><div className="grid min-w-0 grid-cols-1 gap-npc-gap-sm sm:grid-cols-[minmax(136px,0.45fr)_minmax(0,1fr)_auto]"><select aria-label={t('development_task.field.context_kind')} value={context.kind} onChange={(event) => updateContext(index, { kind: event.target.value as ContextKind })} className={fieldClass}>{contextKinds.map((kind) => <option key={kind} value={kind}>{t(contextCopy[kind])}</option>)}</select><input aria-label={t('development_task.field.context_title')} value={context.title} onChange={(event) => updateContext(index, { title: event.target.value })} className={fieldClass} /><Button variant="ghost" size="sm" type="button" aria-label={t('development_task.action.remove')} onClick={() => setContexts((current) => current.filter((_, itemIndex) => itemIndex !== index))}>{t('development_task.action.remove')}</Button></div><input aria-label={t('development_task.field.context_locator')} value={context.locator} onChange={(event) => updateContext(index, { locator: event.target.value })} className={cn(fieldClass, 'font-npc-mono')} /><details><summary className="cursor-pointer text-npc-caption text-npc-ink-soft">{t('development_task.section.advanced')}</summary><div className="mt-npc-gap-sm"><Field label={t('development_task.field.context_digest')}><input value={context.digest} onChange={(event) => updateContext(index, { digest: event.target.value })} className={cn(fieldClass, 'font-npc-mono')} /></Field><p className="mb-0 mt-npc-gap-xs text-npc-meta leading-[1.5] text-npc-ink-softer">{t('development_task.hint.digest')}</p></div></details></div>)}
              <Button variant="secondary" size="sm" type="button" className="justify-self-start" onClick={() => setContexts((current) => [...current, emptyContext()])}>{t('development_task.action.add_context')}</Button>
            </div>
          </section>
        </div>
      </details>

      <details className="border-t border-npc-border-softer pt-npc-card">
        <summary className="cursor-pointer text-npc-body-sm font-semibold text-npc-ink">{t('development_task.field.model')}</summary>
        <p className="mb-0 mt-npc-gap-sm break-words border-l-2 border-npc-blue pl-npc-gap-lg text-npc-caption leading-[1.6] text-npc-ink-soft">
          {t('development_task.hint.model')}
          {advisoryDeveloper ? <code className="ml-npc-gap-sm font-npc-mono text-npc-ink-medium">{advisoryDeveloper.model}</code> : null}
        </p>
      </details>

    </form>
  )
}

function isReadyRepo(repo: DevelopmentTaskRepoOption): boolean {
  return repo.resolve_status === 'ready' && Boolean(repo.base_sha)
}

function RepositoryOptions({
  projectId,
  options,
  repos,
  selectedRepo,
  onSelect,
}: {
  projectId: string
  options: ReturnType<typeof useDevelopmentTaskOptions>
  repos: DevelopmentTaskRepoOption[]
  selectedRepo: string
  onSelect: (repo: string) => void
}) {
  const needsAssociation = options.isSuccess && repos.length === 0
  const account = useAccountMembership(needsAssociation)
  const associations = useProjectRepositories(projectId, needsAssociation)
  const canManage = account.data?.capabilities.includes('project.manage') ?? false
  const candidates = useProjectRepositoryCandidates(
    projectId,
    needsAssociation && associations.isSuccess && associations.data.repositories.length === 0 && canManage,
  )
  const link = useLinkProjectRepository(projectId)

  if (options.isPending) {
    return <p role="status" className="m-0 text-npc-caption text-npc-ink-soft">{t('development_task.repo.loading')}</p>
  }
  if (options.isError) {
    return <RetryNotice message={t('development_task.repo.error')} pending={options.isFetching} onRetry={() => void options.refetch()} />
  }
  if (repos.length === 0) {
    return (
      <RepositoryAssociation
        account={account}
        associations={associations}
        candidates={candidates}
        canManage={canManage}
        link={link}
      />
    )
  }
  if (repos.length === 1) {
    return isReadyRepo(repos[0]!) ? null : (
      <p role="status" className="m-0 border-l-2 border-npc-amber bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink">
        {t('development_task.repo.single_unavailable', { repo: repos[0]!.name })}
      </p>
    )
  }

  const readyCount = repos.filter(isReadyRepo).length
  return (
    <div className="grid min-w-0 gap-npc-gap-xs">
      <Field label={t('development_task.field.repo')}>
        <select value={selectedRepo} onChange={(event) => onSelect(event.target.value)} className={fieldClass}>
          <option value="">{t('development_task.repo.select')}</option>
          {repos.map((repo) => (
            <option key={repo.name} value={repo.name} disabled={!isReadyRepo(repo)}>
              {isReadyRepo(repo) ? repo.name : t('development_task.repo.option_unavailable', { repo: repo.name })}
            </option>
          ))}
        </select>
      </Field>
      <p className="m-0 text-npc-meta leading-[1.5] text-npc-ink-softer">
        {readyCount > 0 ? t('development_task.repo.select_hint') : t('development_task.repo.all_unavailable')}
      </p>
    </div>
  )
}

function RepositoryAssociation({
  account,
  associations,
  candidates,
  canManage,
  link,
}: {
  account: ReturnType<typeof useAccountMembership>
  associations: ReturnType<typeof useProjectRepositories>
  candidates: ReturnType<typeof useProjectRepositoryCandidates>
  canManage: boolean
  link: ReturnType<typeof useLinkProjectRepository>
}) {
  const [selected, setSelected] = useState('')
  const autoLinkedCandidate = useRef<string | null>(null)
  const [autoLinkError, setAutoLinkError] = useState<ApiFailure | null>(null)
  const repositories = candidates.data?.repositories ?? []
  const available = repositories.filter((candidate) => !candidate.empty)
  const emptyCount = repositories.length - available.length
  const associationReady = account.isSuccess && associations.isSuccess && associations.data.repositories.length === 0 && canManage && candidates.isSuccess
  const onlyAvailable = associationReady && available.length === 1 ? available[0] : undefined
  const onlyAvailableIdentity = onlyAvailable ? repositoryCandidateIdentity(onlyAvailable) : null

  useEffect(() => {
    if (!onlyAvailable || !onlyAvailableIdentity || autoLinkError || link.isPending) return
    if (autoLinkedCandidate.current === onlyAvailableIdentity) return

    autoLinkedCandidate.current = onlyAvailableIdentity
    void link.mutateAsync({ source_ref: onlyAvailable.source_ref, source_repo_id: onlyAvailable.source_repo_id })
      .catch((cause: unknown) => setAutoLinkError(
        cause instanceof ApiFailure
          ? cause
          : new ApiFailure('reported', t('development_task.repo.link_error'), { status: 500, code: 'repository_association.unavailable' }),
      ))
  }, [autoLinkError, link, onlyAvailable, onlyAvailableIdentity])

  if (account.isPending || associations.isPending || candidates.isPending && candidates.fetchStatus !== 'idle') {
    return <p role="status" className="m-0 text-npc-caption text-npc-ink-soft">{t('development_task.repo.association_loading')}</p>
  }
  if (account.isError) {
    return <RetryNotice message={t('development_task.repo.account_error')} pending={account.isFetching} onRetry={() => void account.refetch()} />
  }
  if (associations.isError) {
    return <RetryNotice message={t('development_task.repo.association_error')} pending={associations.isFetching} onRetry={() => void associations.refetch()} />
  }
  if (associations.data.repositories.length > 0) {
    return <p role="status" className="m-0 text-npc-caption text-npc-ink-soft">{t('development_task.repo.refreshing')}</p>
  }
  if (!canManage) {
    return <RepositoryStatus>{t('development_task.repo.permission')}</RepositoryStatus>
  }
  if (candidates.error instanceof ApiFailure && candidates.error.status === 403) {
    return <RepositoryStatus>{t('development_task.repo.permission')}</RepositoryStatus>
  }
  if (candidates.isError) {
    return <RetryNotice message={candidateError(candidates.error)} pending={candidates.isFetching} onRetry={() => void candidates.refetch()} />
  }

  const selectedCandidate = available.find((candidate) => repositoryCandidateIdentity(candidate) === selected)
  const linkError = autoLinkError ?? (link.error instanceof ApiFailure ? link.error : null)

  async function submit(candidate: ProjectRepositoryCandidate | undefined) {
    if (!candidate || candidate.empty) return
    try {
      await link.mutateAsync({ source_ref: candidate.source_ref, source_repo_id: candidate.source_repo_id })
    } catch {
      // The controlled error below keeps transport and internal codes off screen.
    }
  }

  function retryAutoLink() {
    autoLinkedCandidate.current = null
    setAutoLinkError(null)
    link.reset()
  }

  if (repositories.length === 0) {
    return (
      <div className="grid min-w-0 gap-npc-gap-sm border-l-2 border-npc-amber bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm">
        <p role="status" className="m-0 text-npc-caption text-npc-amber-ink">{t('development_task.repo.no_candidates')}</p>
        <Button variant="secondary" size="sm" className="justify-self-start" onClick={() => void candidates.refetch()}>
          {t('development_task.action.retry')}
        </Button>
      </div>
    )
  }

  if (onlyAvailable && !autoLinkError) {
    return <RepositoryStatus>{t('development_task.repo.refreshing')}</RepositoryStatus>
  }

  return (
    <div className="grid min-w-0 gap-npc-gap-sm border-l-2 border-npc-amber bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm">
      <div>
        <p className="m-0 text-npc-caption font-semibold text-npc-amber-ink">{t('development_task.repo.none')}</p>
        <p className="mb-0 mt-npc-gap-xs text-npc-meta leading-[1.5] text-npc-ink-soft">{t('development_task.repo.link_hint')}</p>
      </div>

      {available.length === 1 ? (
        autoLinkError ? (
          <RetryNotice message={repositoryLinkError(autoLinkError)} pending={link.isPending} onRetry={retryAutoLink} />
        ) : null
      ) : available.length > 1 ? (
        <div className="grid min-w-0 gap-npc-gap-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <Field label={t('development_task.repo.candidate_label')}>
            <select value={selected} onChange={(event) => { setSelected(event.target.value); link.reset() }} className={fieldClass}>
              <option value="">{t('development_task.repo.candidate_select')}</option>
              {repositories.map((candidate) => (
                <option key={repositoryCandidateIdentity(candidate)} value={repositoryCandidateIdentity(candidate)} disabled={candidate.empty}>
                  {candidate.empty
                    ? t('development_task.repo.candidate_empty_option', { repo: candidate.full_name })
                    : candidate.full_name}
                </option>
              ))}
            </select>
          </Field>
          <Button variant="primary" size="sm" disabled={!selectedCandidate || link.isPending} onClick={() => void submit(selectedCandidate)}>
            {link.isPending ? t('development_task.repo.linking') : t('development_task.repo.link_action')}
          </Button>
        </div>
      ) : (
        <div className="grid min-w-0 gap-npc-gap-xs">
          {repositories.map((candidate) => (
            <Button key={`${candidate.source_ref}:${candidate.source_repo_id}`} variant="secondary" size="sm" className="justify-self-start" disabled>
              {t('development_task.repo.candidate_empty_option', { repo: candidate.full_name })}
            </Button>
          ))}
        </div>
      )}

      {emptyCount > 0 ? <p className="m-0 text-npc-meta leading-[1.5] text-npc-ink-soft">{t('development_task.repo.empty_hint', { count: emptyCount })}</p> : null}
      {linkError && !autoLinkError ? <ErrorNotice message={repositoryLinkError(linkError)} /> : null}
    </div>
  )
}

function repositoryCandidateIdentity(candidate: ProjectRepositoryCandidate): string {
  return JSON.stringify([candidate.source_ref, candidate.source_repo_id])
}

function candidateError(error: unknown): string {
  if (error instanceof ApiFailure && error.status === 503) return t('development_task.repo.candidates_unavailable')
  return t('development_task.repo.candidates_error')
}

function repositoryLinkError(error: ApiFailure): string {
  if (error.status === 403) return t('development_task.repo.permission')
  if (error.status === 409) return t('development_task.repo.link_conflict')
  if (error.status === 503) return t('development_task.repo.link_unavailable')
  return t('development_task.repo.link_error')
}

function RepositoryStatus({ children }: { children: ReactNode }) {
  return <p role="status" className="m-0 border-l-2 border-npc-amber bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink">{children}</p>
}

function RetryNotice({ message, pending, onRetry }: { message: string; pending: boolean; onRetry: () => void }) {
  return (
    <div role="alert" className="flex min-w-0 flex-wrap items-center justify-between gap-npc-gap-sm border-l-2 border-npc-red bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink">
      <span>{message}</span>
      <Button variant="secondary" size="sm" type="button" disabled={pending} onClick={onRetry}>
        {pending ? t('development_task.repo.retrying') : t('development_task.action.retry')}
      </Button>
    </div>
  )
}

type ContextDraft = { kind: ContextKind; title: string; locator: string; digest: string }

function emptyContext(): ContextDraft {
  return { kind: 'product_doc', title: '', locator: '', digest: '' }
}

const fieldClass = cn(
  'w-full min-w-0 rounded-npc-control border border-npc-border bg-npc-panel',
  'px-npc-input-x py-npc-gap-sm text-npc-body text-npc-ink outline-none',
  'transition-colors duration-npc-hover hover:border-npc-border-hover',
  'focus-visible:border-npc-green focus-visible:ring-2 focus-visible:ring-npc-green/20',
)

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block min-w-0 text-npc-caption font-semibold text-npc-ink-medium"><span className="mb-npc-gap-xs block">{label}</span>{children}</label>
}

/**
 * 任务页身份有效期四档，与设置页「AI 与 API」是同一套。
 *
 * **值的合法集合由契约类型守着**（MaintainerOnboardingDuration 来自
 * 生成的 OpenAPI 类型）：契约里加一档而这里没跟上，TS 当场报
 * 「少了一个成员」；这里写了契约没有的档位，同样编不过。
 * 顺序在这儿定，因为那是展示问题，契约不该管。
 */
const MAINTAINER_DURATIONS: readonly MaintainerOnboardingDuration[] = [
  '30d',
  '90d',
  '180d',
  'long_term',
] as const

export function DevelopmentTaskScreen() {
  const { taskId } = useParams({ strict: false }) as { taskId?: string }
  return taskId ? <LoadedDevelopmentTask taskId={taskId} /> : <ScreenLoading shape="detail" />
}

function LoadedDevelopmentTask({ taskId }: { taskId: string }) {
  const task = useDevelopmentTask(taskId)
  const events = useDevelopmentTaskEvents(taskId)
  const cancel = useCancelDevelopmentTask(taskId)
  const claim = useClaimDevelopmentTask(taskId)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [claimOpen, setClaimOpen] = useState(false)
  const [claimResult, setClaimResult] = useState<DevelopmentTaskClaim | null>(null)

  function closeClaim(open: boolean) {
    setClaimOpen(open)
    if (!open) {
      setClaimResult(null)
      claim.reset()
    }
  }

  if (task.isPending || !task.data) return <ScreenLoading shape="detail" />
  if (task.isError) return <TaskRequestError error={task.error} retry={() => void task.refetch()} />

  const taskData = task.data
  return (
    <Page>
      <PageHeader
        breadcrumb={<span>{t('development_task.create.title')}</span>}
        title={taskData.title}
        subtitle={<code className="break-all font-npc-mono text-npc-body-sm">{taskData.id}</code>}
        actions={<Button variant="danger" size="sm" onClick={() => setConfirmCancel(true)} disabled={isDevelopmentTaskFinal(taskData.state)}>{t('development_task.action.cancel')}</Button>}
        align="start"
      />
      <div className="mt-npc-section grid min-w-0 grid-cols-1 gap-npc-grid-lg lg:grid-cols-[minmax(0,1.5fr)_minmax(300px,0.8fr)]">
        <div className="min-w-0 space-y-npc-grid-lg">
          <DeliveryMissionFromPreviewLane
            task={taskData}
            loadReceipt={getDevelopmentTaskPreviewReceipt}
            loadRelease={getDevelopmentTaskReleaseReceipt}
          />
          <TaskOverview task={taskData} />
          {taskData.state === 'draft' && taskData.code_context_status === 'preparing' ? <TaskPreparation task={taskData} /> : null}
          <TaskPacket task={taskData} />
          <StageCard task={taskData} events={events.data ?? []} />
          <TaskTimeline events={events.data ?? []} pending={events.isPending} error={events.error} retry={() => void events.refetch()} />
        </div>
        <div className="min-w-0 space-y-npc-grid-lg">
          <MagicLinkPanel task={taskData} />
          <PreviewLanePanel task={taskData} />
          <ClaimPanel task={taskData} onClaim={() => setClaimOpen(true)} />
          <TaskDetails task={taskData} />
          <TaskComments taskId={taskData.id} />
        </div>
      </div>
      <CancelModal open={confirmCancel} onOpenChange={setConfirmCancel} cancel={cancel} />
      <ClaimModal open={claimOpen} onOpenChange={closeClaim} claim={claim} result={claimResult} onClaimed={setClaimResult} />
    </Page>
  )
}

function MagicLinkPanel({ task }: { task: DevelopmentTask }) {
  const account = useAccountMembership()
  const [pending, setPending] = useState(false)
  const [link, setLink] = useState('')
  const [error, setError] = useState('')
  const [maintainerPending, setMaintainerPending] = useState(false)
  const [maintainerLabel, setMaintainerLabel] = useState('Terra NPC Maintainer')
  const [maintainerDuration, setMaintainerDuration] = useState<MaintainerOnboardingDuration>('90d')
  const [maintainerCredentialId, setMaintainerCredentialId] = useState('')
  const [maintainerBundle, setMaintainerBundle] = useState('')
  const enabled = task.state === 'ready_to_claim' && task.code_context_status === 'ready'
  const canManageMaintainer = account.data?.capabilities.includes('vendor.manage') ?? false

  async function issue() {
    setPending(true)
    setError('')
    setLink('')
    try {
      const issued = await createDevelopmentTaskMagicLink(task.id)
      const next = `${window.location.origin}/ai-task#${encodeURIComponent(issued.token)}`
      setLink(next)
      /*
       * 复制不成不能说成「生成失败」。
       *
       * 原来这一行是 `await navigator.clipboard.writeText(next)`，它一抛就落进
       * 下面那个 catch，而那个 catch 报的是 `magic.issue_error`（生成失败）。
       * 可链接**已经签出来了**、也已经渲染在下面那个 code 块里——
       * 照那句报错去做的人会再签一次，把上一把作废掉。
       *
       * 现在这一层不抛，失败是一个返回值，报的是「没能复制，请手动选择下面
       * 这段内容」——那正好是它此刻该做的事。
       */
      if (!(await copyText(next)).ok) setError(t('development_task.hint.copy_failed'))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('development_task.magic.issue_error'))
    } finally {
      setPending(false)
    }
  }

  async function issueMaintainer() {
    if (!maintainerLabel.trim()) return
    setMaintainerPending(true)
    setError('')
    try {
      const issued = await createDevelopmentTaskMaintainerOnboarding(
        task.id, maintainerLabel, maintainerDuration, maintainerCredentialId || undefined,
      )
      // The server owns the fragment; the browser only makes its canonical
      // relative link shareable from this console origin.
      const taskLink = new URL(issued.task_link, window.location.origin).toString()
      const bundle = renderDevelopmentTaskMaintainerPrompt(issued, taskLink)
      setMaintainerCredentialId(issued.credential_id)
      setMaintainerBundle(bundle)
      // 同上：凭据已经签出来了，复制没成不是「签发失败」。
      // 整段 bundle 就渲染在下面那个 pre 里，手动选中拿得到。
      if (!(await copyText(bundle)).ok) setError(t('development_task.hint.copy_failed'))
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('development_task.magic.issue_error'))
    } finally {
      setMaintainerPending(false)
    }
  }

  async function revokeMaintainer() {
    if (!maintainerCredentialId) return
    setMaintainerPending(true)
    setError('')
    try {
      await revokeDevelopmentTaskMaintainerCredential(maintainerCredentialId)
      setMaintainerCredentialId('')
      setMaintainerBundle('')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('development_task.magic.issue_error'))
    } finally {
      setMaintainerPending(false)
    }
  }

  return (
    <Card data-testid="development-task-magic-link">
      <CardTitle>{t('development_task.magic.panel.title')}</CardTitle>
      <CardHint className="mt-npc-gap-sm leading-[1.6]">{t('development_task.magic.panel.hint')}</CardHint>
      <Button variant="primary" className="mt-npc-card" disabled={!enabled || pending} onClick={() => void issue()}>
        {pending ? t('development_task.magic.issue.pending') : link ? t('development_task.magic.issue.renew') : t('development_task.magic.issue.action')}
      </Button>
      {link ? <code className="mt-npc-gap-lg block break-all border-l-2 border-npc-blue bg-npc-surface p-npc-grid-sm font-npc-mono text-npc-caption text-npc-ink" data-testid="development-task-magic-link-value">{link}</code> : null}
      <div className="mt-npc-card border-t border-npc-border pt-npc-card">
        <CardTitle>{t('development_task.maintainer.panel.title')}</CardTitle>
        <CardHint className="mt-npc-gap-sm leading-[1.6]">{t('development_task.maintainer.panel.hint')}</CardHint>
        <div className="mt-npc-card grid gap-npc-gap-sm">
          <label className="grid gap-npc-gap-xs text-npc-caption font-semibold">{t('development_task.maintainer.label')}<input value={maintainerLabel} onChange={(event) => setMaintainerLabel(event.target.value)} maxLength={120} disabled={!canManageMaintainer} className="min-h-11 border border-npc-border bg-white px-npc-grid-sm text-npc-body-sm outline-none focus:border-npc-blue" placeholder={t('development_task.maintainer.placeholder')} /></label>
          <label className="grid gap-npc-gap-xs text-npc-caption font-semibold">{t('development_task.maintainer.duration.label')}<select value={maintainerDuration} onChange={(event) => setMaintainerDuration(event.target.value as MaintainerOnboardingDuration)} disabled={!canManageMaintainer} className="min-h-11 border border-npc-border bg-white px-npc-grid-sm text-npc-body-sm outline-none focus:border-npc-blue">{MAINTAINER_DURATIONS.map((value) => <option key={value} value={value}>{t(`development_task.maintainer.duration.${value}`)}</option>)}</select></label>
          <div className="flex flex-wrap gap-npc-gap-sm"><Button variant="secondary" disabled={!enabled || !canManageMaintainer || maintainerPending || !maintainerLabel.trim()} onClick={() => void issueMaintainer()}>{maintainerPending ? t('development_task.maintainer.pending') : maintainerCredentialId ? t('development_task.maintainer.rotate') : t('development_task.maintainer.issue')}</Button>{maintainerCredentialId ? <Button variant="danger" disabled={!canManageMaintainer || maintainerPending} onClick={() => void revokeMaintainer()}>{t('development_task.maintainer.revoke')}</Button> : null}</div>
        </div>
        {maintainerBundle ? <pre className="mt-npc-gap-lg max-h-[360px] overflow-auto whitespace-pre-wrap break-words border-l-2 border-npc-green bg-npc-surface p-npc-grid-sm font-npc-mono text-npc-meta leading-[1.6]" data-testid="development-task-maintainer-bundle">{maintainerBundle}</pre> : null}
      </div>
      {error ? <ErrorNotice message={error} /> : null}
    </Card>
  )
}

function TaskRequestError({ error, retry }: { error: unknown; retry: () => void }) {
  return <Page><Card tone="amber" className="max-w-[560px]"><p className="m-0 text-npc-body-sm">{error instanceof Error ? error.message : t('development_task.hint.request_error')}</p><Button variant="secondary" size="sm" className="mt-npc-gap-lg" onClick={retry}>{t('development_task.action.retry')}</Button></Card></Page>
}

function TaskOverview({ task }: { task: DevelopmentTask }) {
  const preparing = task.state === 'draft' && task.code_context_status === 'preparing'
  const next = taskNextActionOf(task)
  const nextLabel = preparing ? t('development_task.state.preparing') : t(userStateCopy(next))
  const liveSha = task.deployment_sha ?? task.candidate_sha ?? task.current_attempt?.result_sha
  const rebase = taskNeedsRebase(task)
  return (
    <Card data-testid="development-task-overview">
      <div className="flex flex-wrap items-start justify-between gap-npc-gap-lg">
        <div className="min-w-0">
          <CardTitle>{t('development_task.section.status')}</CardTitle>
          <div className="mt-npc-gap-sm flex flex-wrap items-center gap-npc-gap-sm">
            <StatusDot tone={stateTone(next)} pulsing={!isDevelopmentTaskFinal(task.state)} />
            <Tag tone={stateTone(next)} data-testid="development-task-next-action">{nextLabel}</Tag>
          </div>
        </div>
        <p className="mb-0 max-w-[280px] text-right text-npc-caption leading-[1.6] text-npc-ink-soft">
          {preparing ? t('development_task.hint.preparing') : t(userStateCopy(next))}
        </p>
      </div>
      <dl className="mt-npc-card grid min-w-0 gap-npc-gap-sm text-npc-caption">
        <Detail label={t('development_task.detail.live_sha')} value={liveSha} mono />
        <Detail label={t('development_task.detail.release_id')} value={task.release_id} mono />
      </dl>
      {rebase ? (
        <p role="status" data-testid="development-task-needs-rebase" className="mt-npc-gap-sm mb-0 border-l-2 border-npc-amber bg-npc-amber-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-amber-ink">
          {t('development_task.detail.needs_rebase')}
        </p>
      ) : null}
    </Card>
  )
}

const PREPARE_AUTO_RETRY_LIMIT = 3
const PREPARE_AUTO_RETRY_DELAYS = [500, 1_000] as const

type PrepareRetryLoop = {
  cancelled: boolean
  timer: ReturnType<typeof setTimeout> | null
  wake: (() => void) | null
}

function TaskPreparation({ task }: { task: DevelopmentTask }) {
  const preparing = task.state === 'draft' && task.code_context_status === 'preparing'
  const options = useDevelopmentTaskOptions(task.project_id)
  const { mutateAsync, isPending, error, reset } = usePrepareDevelopmentTask(task.id)
  const [selectedRepo, setSelectedRepo] = useState('')
  const [automaticPaused, setAutomaticPaused] = useState(false)
  const automaticLoop = useRef<PrepareRetryLoop | null>(null)
  const automaticStartedForTask = useRef<string | null>(null)
  const manualRequestInFlight = useRef(false)
  const mounted = useRef(true)
  const lifecycleGeneration = useRef(0)
  const repos = useMemo(() => options.data?.repos ?? [], [options.data?.repos])
  const boundRepo = task.repo ?? undefined
  const needsOptions = !boundRepo
  const needsAssociation = needsOptions && options.isSuccess && repos.length === 0
  const account = useAccountMembership(needsAssociation)
  const associations = useProjectRepositories(task.project_id, needsAssociation)
  const canManage = account.data?.capabilities.includes('project.manage') ?? false
  const candidates = useProjectRepositoryCandidates(
    task.project_id,
    needsAssociation && associations.isSuccess && associations.data.repositories.length === 0 && canManage,
  )
  const link = useLinkProjectRepository(task.project_id)
  const needsSelection = !boundRepo && repos.length > 1
  const automaticRepo = boundRepo ?? (repos.length === 1 ? repos[0]?.name : undefined)

  const cancelAutomaticRetry = useCallback(() => {
    const loop = automaticLoop.current
    if (!loop) return
    loop.cancelled = true
    if (loop.timer) clearTimeout(loop.timer)
    loop.timer = null
    loop.wake?.()
    loop.wake = null
    automaticLoop.current = null
  }, [])

  const waitForRetry = useCallback((loop: PrepareRetryLoop, delay: number) => new Promise<void>((resolve) => {
    loop.wake = resolve
    loop.timer = setTimeout(() => {
      loop.timer = null
      loop.wake = null
      resolve()
    }, delay)
  }), [])

  const startAutomaticRetry = useCallback((repo?: string) => {
    cancelAutomaticRetry()
    const loop: PrepareRetryLoop = { cancelled: false, timer: null, wake: null }
    automaticLoop.current = loop
    if (mounted.current) setAutomaticPaused(false)

    void (async () => {
      for (let attempt = 0; attempt < PREPARE_AUTO_RETRY_LIMIT; attempt += 1) {
        if (loop.cancelled) return
        if (attempt > 0) {
          const delay = PREPARE_AUTO_RETRY_DELAYS[attempt - 1]
          if (delay === undefined) return
          await waitForRetry(loop, delay)
        }
        if (loop.cancelled) return

        try {
          const prepared = await mutateAsync(repo ? { repo } : {})
          if (loop.cancelled || prepared.code_context_status === 'ready') return
        } catch {
          if (loop.cancelled) return
        }
      }

      if (!loop.cancelled && automaticLoop.current === loop) {
        automaticLoop.current = null
        if (mounted.current) setAutomaticPaused(true)
      }
    })()
  }, [cancelAutomaticRetry, mutateAsync, waitForRetry])

  useEffect(() => {
    const generation = lifecycleGeneration.current + 1
    lifecycleGeneration.current = generation
    mounted.current = true
    return () => {
      mounted.current = false
      queueMicrotask(() => {
        // React StrictMode immediately replays effects without destroying the
        // component instance. Keep that request and its retry budget alive;
        // a real unmount has no following generation and is cancelled here.
        if (!mounted.current && lifecycleGeneration.current === generation) cancelAutomaticRetry()
      })
    }
  }, [cancelAutomaticRetry])

  useEffect(() => {
    if (!preparing || (needsOptions && (options.isPending || options.isError)) || needsAssociation || needsSelection || automaticStartedForTask.current === task.id) return
    automaticStartedForTask.current = task.id
    startAutomaticRetry(automaticRepo)
  }, [automaticRepo, needsAssociation, needsOptions, needsSelection, options.isError, options.isPending, preparing, startAutomaticRetry, task.id])

  function retry() {
    if (!preparing || needsAssociation || manualRequestInFlight.current) return
    manualRequestInFlight.current = true
    cancelAutomaticRetry()
    reset()
    const repo = needsSelection ? selectedRepo : automaticRepo
    void mutateAsync(repo ? { repo } : {})
      .catch(() => undefined)
      .finally(() => {
        manualRequestInFlight.current = false
      })
  }

  if (!preparing) return null

  return (
      <Card data-testid="development-task-preparation">
      <CardTitle>{t('development_task.state.preparing')}</CardTitle>
      <CardHint className="mt-npc-gap-sm leading-[1.6]">{t('development_task.hint.preparing_packet')}</CardHint>
      {needsOptions && options.isError ? <RetryNotice message={t('development_task.repo.error')} pending={options.isFetching} onRetry={() => void options.refetch()} /> : null}
      {needsAssociation ? (
        <div className="mt-npc-card">
          <RepositoryAssociation
            account={account}
            associations={associations}
            candidates={candidates}
            canManage={canManage}
            link={link}
          />
        </div>
      ) : null}
      {needsSelection ? (
        <div className="mt-npc-card grid min-w-0 gap-npc-gap-sm sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
          <Field label={t('development_task.field.repo')}>
            <select value={selectedRepo} onChange={(event) => setSelectedRepo(event.target.value)} className={fieldClass}>
              <option value="">{t('development_task.repo.select')}</option>
              {repos.map((repo) => <option key={repo.name} value={repo.name}>{repo.name}</option>)}
            </select>
          </Field>
          <Button variant="secondary" disabled={!selectedRepo || isPending} onClick={retry}>{isPending ? t('development_task.action.preparing') : t('development_task.action.prepare_repo')}</Button>
        </div>
      ) : null}
      {error || automaticPaused ? <ErrorNotice message={t(needsSelection ? 'development_task.error.prepare_select_repo' : automaticPaused ? 'development_task.error.prepare_paused' : 'development_task.error.prepare_retrying')} /> : null}
      <div className="mt-npc-card flex flex-wrap justify-end gap-npc-gap-sm">
        <Button variant="secondary" disabled={isPending || needsAssociation || (needsSelection && !selectedRepo)} onClick={retry}>{isPending ? t('development_task.action.preparing') : t('development_task.action.retry')}</Button>
      </div>
    </Card>
  )
}

function ClaimPanel({ task, onClaim }: { task: DevelopmentTask; onClaim: () => void }) {
  const repair = task.state === 'fix_requested' || task.state === 'failed' || taskNeedsRebase(task)
  const enabled = task.code_context_status === 'ready' && task.state !== 'cancelled' && (
    task.state === 'ready_to_claim' || task.state === 'fix_requested' || task.state === 'failed' || taskNeedsRebase(task)
  )
  return (
    <Card data-testid="development-task-claim">
      <CardTitle>{repair ? t('development_task.action.repair') : t('development_task.claim.title')}</CardTitle>
      <CardHint className="mt-npc-gap-sm leading-[1.6]">{task.code_context_status === 'preparing' ? t('development_task.hint.preparing_claim') : t('development_task.hint.claim')}</CardHint>
      <Button variant="primary" className="mt-npc-card" disabled={!enabled} onClick={onClaim}>{repair ? t('development_task.action.repair') : t('development_task.action.claim')}</Button>
    </Card>
  )
}

function ClaimModal({ open, onOpenChange, claim, result, onClaimed }: { open: boolean; onOpenChange: (open: boolean) => void; claim: ReturnType<typeof useClaimDevelopmentTask>; result: DevelopmentTaskClaim | null; onClaimed: (result: DevelopmentTaskClaim) => void }) {
  const [executorId, setExecutorId] = useState(defaultExecutorId)
  const [executorProvider, setExecutorProvider] = useState('')
  const [executorModel, setExecutorModel] = useState('')
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const claimed = result !== null
  const credentialText = result ? claimText(result) : ''
  const identity = completeExecutorIdentity(executorProvider, executorModel)
  const identityPartial = hasPartialExecutorIdentity(executorProvider, executorModel)

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!executorId.trim()) return
    try {
      onClaimed(await claim.claim(executorId.trim(), identity))
    } catch {
      // The API error already carries a copy-template-rendered message.
    }
  }

  async function copy() {
    setCopyState((await copyText(credentialText)).ok ? 'copied' : 'failed')
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange} title={t('development_task.claim.title')} description={t('development_task.claim.subtitle')} className="w-[min(720px,calc(100vw-32px))]">
      {claimed ? (
        <div data-testid="development-task-credential" className="grid min-w-0 gap-npc-gap-lg">
          <p data-testid="development-task-claim-next" className="m-0 text-npc-body-sm text-npc-ink">{t('development_task.user_state.in_progress')}</p>
          <div className="flex flex-wrap justify-end gap-npc-gap-sm"><Button variant="primary" onClick={() => void copy()}>{copyState === 'copied' ? t('development_task.action.copied') : t('development_task.action.copy')}</Button></div>
          {copyState === 'failed' ? <ErrorNotice message={t('development_task.hint.copy_failed')} /> : null}
          <details className="border-t border-npc-border-softer pt-npc-card">
            <summary className="cursor-pointer text-npc-body-sm font-semibold text-npc-ink">{t('development_task.section.advanced')}</summary>
            <div className="mt-npc-card grid min-w-0 gap-npc-gap-lg">
              <Credential label={t('development_task.claim.endpoint')} value={result.claim.endpoint} />
              <Credential label={t('development_task.claim.username')} value={result.claim.username} />
              <Credential label={t('development_task.claim.token')} value={result.claim.token} secret />
              <Credential label={t('development_task.claim.fingerprint')} value={result.claim.fingerprint} />
              <Credential label={t('development_task.claim.expiry')} value={timestamp(result.claim.expires_at)} />
              <TaskPacket task={result.task_packet} compact />
              <pre className="m-0 max-w-full overflow-x-auto whitespace-pre-wrap break-words border-l-2 border-npc-blue bg-npc-surface p-npc-grid-sm font-npc-mono text-npc-meta leading-[1.6] text-npc-ink-medium">{credentialText}</pre>
            </div>
          </details>
        </div>
      ) : (
        <form className="grid min-w-0 gap-npc-gap-lg" onSubmit={(event) => void submit(event)}>
          <Field label={t('development_task.field.executor')}><input value={executorId} onChange={(event) => setExecutorId(event.target.value)} className={fieldClass} /></Field>
          <details data-testid="development-task-executor-identity" className="border-t border-npc-border-softer pt-npc-card">
            <summary className="cursor-pointer text-npc-body-sm font-semibold text-npc-ink">{t('development_task.section.executor_identity')}</summary>
            <div className="mt-npc-card grid min-w-0 gap-npc-gap-sm">
              <p className="m-0 text-npc-caption leading-[1.6] text-npc-ink-soft">{t('development_task.hint.executor_identity')}</p>
              <div className="grid min-w-0 gap-npc-gap-sm sm:grid-cols-2">
                <Field label={t('development_task.field.executor_provider')}><input value={executorProvider} onChange={(event) => setExecutorProvider(event.target.value)} className={fieldClass} /></Field>
                <Field label={t('development_task.field.executor_model')}><input value={executorModel} onChange={(event) => setExecutorModel(event.target.value)} className={fieldClass} /></Field>
              </div>
              {identityPartial ? <p role="status" className="m-0 text-npc-caption text-npc-amber-ink">{t('development_task.hint.executor_identity_partial')}</p> : null}
            </div>
          </details>
          {claim.error ? <ErrorNotice message={claim.error} /> : null}
          <div className="flex flex-wrap justify-end gap-npc-gap-sm"><Button variant="primary" type="submit" disabled={!executorId.trim() || claim.isPending}>{claim.isPending ? t('development_task.action.claiming') : t('development_task.action.claim')}</Button></div>
        </form>
      )}
    </Modal>
  )
}

function CancelModal({ open, onOpenChange, cancel }: { open: boolean; onOpenChange: (open: boolean) => void; cancel: ReturnType<typeof useCancelDevelopmentTask> }) {
  return <Modal open={open} onOpenChange={onOpenChange} title={t('development_task.cancel.title')} description={t('development_task.cancel.body')} footer={<><Button variant="secondary" onClick={() => onOpenChange(false)} disabled={cancel.isPending}>{t('development_task.action.keep')}</Button><Button variant="danger" disabled={cancel.isPending} onClick={() => void cancel.mutateAsync().then(() => onOpenChange(false)).catch(() => undefined)}>{cancel.isPending ? t('development_task.action.cancelling') : t('development_task.action.confirm_cancel')}</Button></>}>
    {cancel.error ? <ErrorNotice message={cancel.error.message} /> : null}
  </Modal>
}

function Credential({ label, value, secret = false }: { label: string; value: string; secret?: boolean }) {
  return <div className="min-w-0"><div className="text-npc-caption font-semibold text-npc-ink-medium">{label}</div><code className={cn('mt-npc-gap-xs block break-all border-l-2 border-npc-border bg-npc-surface p-npc-grid-sm font-npc-mono text-npc-caption text-npc-ink', secret && 'select-all')}>{value}</code></div>
}

function TaskPacket({ task, compact = false }: { task: Pick<DevelopmentTask, 'title' | 'instructions' | 'acceptance_criteria' | 'work_domains' | 'context_items' | 'recommended_context_kinds' | 'packet_version' | 'packet_digest' | 'base_ref' | 'base_sha' | 'target_ref' | 'repo'>; compact?: boolean }) {
  const git = [task.repo, task.base_ref].filter((value): value is string => Boolean(value)).join(' · ')
  const packet = task.packet_version !== null && task.packet_digest ? `${task.packet_version} · ${task.packet_digest}` : null
  const content = <>
    <div className="min-w-0"><div className="text-npc-caption font-semibold text-npc-ink-medium">{t('development_task.field.instructions')}</div><p className="mb-0 mt-npc-gap-xs whitespace-pre-wrap break-words text-npc-caption leading-[1.6] text-npc-ink-soft">{task.instructions}</p></div>
    <div><div className="text-npc-caption font-semibold text-npc-ink-medium">{t('development_task.field.acceptance')}</div><ul className="mb-0 mt-npc-gap-sm grid list-disc gap-npc-gap-xs pl-npc-grid-lg text-npc-caption leading-[1.6] text-npc-ink-soft">{task.acceptance_criteria.map((criterion, index) => <li key={index}>{criterion.text}</li>)}</ul></div>
    <details className="border-t border-npc-border-softer pt-npc-card">
      <summary className="cursor-pointer text-npc-body-sm font-semibold text-npc-ink">{t('development_task.section.advanced')}</summary>
      <div className="mt-npc-card grid min-w-0 gap-npc-card">
        <ContextGroups attached={task.context_items} recommended={task.recommended_context_kinds} />
        <dl className="mb-0 grid grid-cols-[minmax(96px,0.4fr)_minmax(0,1fr)] gap-x-npc-gap-lg gap-y-npc-gap-sm text-npc-caption"><Detail label={t('development_task.section.git')} value={git || null} mono /><Detail label={t('development_task.field.base_sha')} value={task.base_sha} mono /><Detail label={t('development_task.section.packet')} value={packet} mono /><Detail label={t('development_task.field.context_locator')} value={task.target_ref} mono /></dl>
      </div>
    </details>
  </>
  if (compact) return <section className="border-t border-npc-border-softer pt-npc-card"><h3 className="m-0 text-npc-body-sm font-semibold text-npc-ink">{t('development_task.section.packet')}</h3><div className="mt-npc-card grid min-w-0 gap-npc-card">{content}</div></section>
  return <Card><CardTitle>{t('development_task.section.packet')}</CardTitle><div className="mt-npc-card grid min-w-0 gap-npc-card">{content}</div></Card>
}

function ContextGroups({ attached, recommended }: { attached: DevelopmentTask['context_items']; recommended: DevelopmentTask['recommended_context_kinds'] }) {
  return <div className="grid min-w-0 gap-npc-gap-lg sm:grid-cols-2"><div><div className="text-npc-caption font-semibold text-npc-ink-medium">{t('development_task.section.attached')}</div>{attached.length ? <ul className="mb-0 mt-npc-gap-sm grid list-none gap-npc-gap-xs p-0 text-npc-caption text-npc-ink-soft">{attached.map((item, index) => <li key={`${item.kind}-${index}`} className="break-words">{t(contextCopy[item.kind])} · {item.title}</li>)}</ul> : <p className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-softer">{t('development_task.hint.no_context')}</p>}</div><div><div className="text-npc-caption font-semibold text-npc-ink-medium">{t('development_task.section.recommended')}</div>{recommended.length ? <div className="mt-npc-gap-sm flex flex-wrap gap-npc-gap-xs">{recommended.map((kind) => <Tag key={kind}>{t(contextCopy[kind])}</Tag>)}</div> : <p className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-softer">{t('development_task.hint.no_recommendation')}</p>}</div></div>
}

function TaskDetails({ task }: { task: DevelopmentTask }) {
  return <Card><CardTitle>{t('development_task.section.details')}</CardTitle><div className="mt-npc-card grid min-w-0 gap-npc-card"><div className="text-npc-caption font-semibold text-npc-ink-medium">{task.title}</div>{task.work_domains.length ? <div className="flex flex-wrap gap-npc-gap-xs">{task.work_domains.map((domain) => <Tag key={domain}>{t(domainCopy[domain])}</Tag>)}</div> : null}<Link to="/projects/$projectId" params={{ projectId: task.project_id }} className="text-npc-caption text-npc-ink-soft hover:text-npc-ink">{t('nav.project')}</Link></div></Card>
}

const memoryKindCopy: Record<string, VarlessCopyId> = {
  tried: 'development_task.memory.kind.tried',
  failed_because: 'development_task.memory.kind.failed_because',
  comment: 'development_task.memory.kind.comment',
  note: 'development_task.memory.kind.note',
}

/**
 * 「没修好？告诉它」（修复任务闭环 §C §D）。
 *
 * 一条评论 = 一次续修请求：任务在 ready / failed 时回到可领，轮数 +1；
 * 到上限转人。下面那段「它试过什么」是这条任务自己的记忆，
 * 下一轮模型看到的就是这几条。
 *
 * **评论与记忆里的长文本用 `wrap-anywhere`，不是 `break-words`。**
 * 这两个都是换行，差别在 min-content：执行器写回来的失败摘要全是
 * `apps/console/src/...` 这种长路径，中间只有 `/`，而浏览器不把 `/`
 * 当断点——于是最长不可断段有 50 多字符、约 318px。`break-words`
 * （`overflow-wrap: break-word`）**不参与 min-content 计算**，那 318px
 * 会一路顶上去：右栏被 `minmax(300px, 0.8fr)` 压在 300px 时内容盒只有
 * 263px，整张卡被撑破，1024 宽度下还会把整页顶出横向滚动条。
 * `wrap-anywhere`（`overflow-wrap: anywhere`）会把 min-content 算到 1 字符，
 * 卡因此能收进栏里；正常文本的断行观感不变，只在必须断时才断。
 */
export function TaskComments({ taskId }: { taskId: string }) {
  const comments = useDevelopmentTaskComments(taskId)
  const add = useAddDevelopmentTaskComment(taskId)
  const [draft, setDraft] = useState('')
  const [outcome, setOutcome] = useState<'recorded' | 'reopened' | 'exhausted' | null>(null)
  const data = comments.data
  const submit = () => {
    const text = draft.trim()
    if (!text) return
    add.mutate(text, {
      onSuccess: (next) => {
        setDraft('')
        setOutcome(next.outcome ?? 'recorded')
      },
    })
  }
  return (
    <Card data-testid="development-task-comments">
      <CardTitle>{t('development_task.comments.title')}</CardTitle>
      <div className="mt-npc-card grid min-w-0 gap-npc-card">
        {data ? (
          <p data-testid="development-task-comments-rounds" className="m-0 text-npc-meta text-npc-ink-softer">
            <T id="development_task.comments.rounds" vars={{ rounds: String(data.repair_rounds), max: String(data.max_repair_rounds) }} />
          </p>
        ) : null}
        {data && data.comments.length === 0 ? <p className="m-0 text-npc-caption text-npc-ink-soft">{t('development_task.comments.empty')}</p> : null}
        {data && data.comments.length ? (
          <ol data-testid="development-task-comment-list" className="m-0 grid list-none gap-npc-gap-sm p-0">
            {data.comments.map((c) => (
              <li key={c.id} className="wrap-anywhere text-npc-caption text-npc-ink">
                <span className="text-npc-meta text-npc-ink-softer">{timestamp(c.created_at)}</span> · {c.body}
              </li>
            ))}
          </ol>
        ) : null}
        <textarea
          data-testid="development-task-comment-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('development_task.comments.placeholder')}
          className="min-h-[72px] w-full border border-npc-border-softer bg-npc-surface p-npc-grid-sm text-npc-caption text-npc-ink"
        />
        <div className="flex flex-wrap items-center gap-npc-gap-sm">
          <Button size="sm" data-testid="development-task-comment-submit" disabled={add.isPending || !draft.trim()} onClick={submit}>
            {t('development_task.comments.submit')}
          </Button>
          {outcome ? (
            <span data-testid="development-task-comment-outcome" data-outcome={outcome} className="text-npc-caption text-npc-ink-soft">
              {outcome === 'exhausted' ? (
                <T id="development_task.comments.outcome_exhausted" vars={{ max: String(data?.max_repair_rounds ?? 5) }} />
              ) : (
                t(outcome === 'reopened' ? 'development_task.comments.outcome_reopened' : 'development_task.comments.outcome_recorded')
              )}
            </span>
          ) : null}
          {add.isError ? <span role="alert" className="text-npc-caption text-npc-red-ink">{t('development_task.comments.failed')}</span> : null}
        </div>
        <div>
          <div className="text-npc-caption font-semibold text-npc-ink-medium">{t('development_task.memory.title')}</div>
          {data && data.memory.length === 0 ? <p className="mb-0 mt-npc-gap-xs text-npc-caption text-npc-ink-softer">{t('development_task.memory.empty')}</p> : null}
          {data && data.memory.length ? (
            <ul data-testid="development-task-memory" className="mb-0 mt-npc-gap-sm grid list-none gap-npc-gap-xs p-0 text-npc-caption text-npc-ink-soft">
              {data.memory.map((m, i) => (
                <li key={i} className="wrap-anywhere">
                  <Tag>{t(memoryKindCopy[m.kind] ?? 'development_task.memory.kind.note')}</Tag> {m.body}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </Card>
  )
}

const stageCopy: Record<StageKey, VarlessCopyId> = {
  claimed: 'development_task.stagecard.claimed',
  delivered: 'development_task.stagecard.delivered',
  reviewing: 'development_task.stagecard.reviewing',
  ci: 'development_task.stagecard.ci',
  decision: 'development_task.stagecard.decision',
  ready: 'development_task.stagecard.ready',
  live: 'development_task.stagecard.live',
  fix_requested: 'development_task.stagecard.fix_requested',
  failed: 'development_task.stagecard.failed',
  cancelled: 'development_task.stagecard.cancelled',
}

/**
 * 流程卡（修复任务闭环 §E）：走到哪了、每一格多久、当前那格在闪。
 * 每一格的时间来自一行事件；没有事件就没有格，不编。
 */
function StageCard({ task, events }: { task: DevelopmentTask; events: DevelopmentTaskEvent[] }) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(id)
  }, [])
  const stages = useMemo(() => stagesOf(task, events, now), [task, events, now])
  const total = stages.length ? now.getTime() - Date.parse(stages[0]!.at) : 0
  return (
    <Card data-testid="development-task-stages">
      <CardTitle>{t('development_task.stages.title')}</CardTitle>
      <div className="mt-npc-card">
        {stages.length === 0 ? (
          <p className="m-0 text-npc-caption text-npc-ink-soft">{t('development_task.stages.empty')}</p>
        ) : (
          <>
            <p data-testid="development-task-stages-total" className="m-0 mb-npc-gap-sm text-npc-meta text-npc-ink-softer">
              <T id="development_task.stages.total" vars={{ elapsed: formatElapsed(total) }} />
            </p>
            <ol className="m-0 flex list-none flex-wrap gap-npc-gap-sm p-0">
              {stages.map((s, i) => (
                <li
                  key={`${s.key}-${s.attempt}-${i}`}
                  data-testid="development-task-stage"
                  data-stage={s.key}
                  data-current={s.current ? 'yes' : 'no'}
                  className={cn(
                    'grid min-w-[104px] gap-npc-gap-xs border border-npc-border-softer px-npc-grid-sm py-npc-gap-sm text-npc-caption',
                    s.current && 'border-npc-green',
                  )}
                >
                  <div className="flex items-center gap-npc-gap-xs">
                    <StatusDot tone={s.tone} className={cn(s.current && 'animate-npc-pulse')} />
                    <span className="font-medium text-npc-ink">{t(stageCopy[s.key])}</span>
                    {s.attempt > 1 ? <span className="text-npc-meta text-npc-ink-softer"><T id="development_task.stages.attempt" vars={{ n: String(s.attempt) }} /></span> : null}
                  </div>
                  <time dateTime={s.at} className="text-npc-meta text-npc-ink-softer">{timestamp(s.at)}</time>
                  <span data-testid="development-task-stage-elapsed" className="font-npc-mono text-npc-meta text-npc-ink-soft">{formatElapsed(s.elapsedMs)}</span>
                </li>
              ))}
            </ol>
            {stages.some((s) => s.key === 'live') ? <p className="mb-0 mt-npc-gap-sm text-npc-meta text-npc-ink-softer">{t('development_task.stages.live_note')}</p> : null}
          </>
        )}
      </div>
    </Card>
  )
}

function TaskTimeline({ events, pending, error, retry }: { events: DevelopmentTaskEvent[]; pending: boolean; error: unknown; retry: () => void }) {
  return <Card data-testid="development-task-timeline"><CardTitle>{t('development_task.section.timeline')}</CardTitle><div className="mt-npc-card">{pending && events.length === 0 ? <p className="m-0 text-npc-caption text-npc-ink-soft">{t('development_task.hint.timeline_loading')}</p> : null}{error ? <div className="border-l-2 border-npc-amber bg-npc-amber-bg p-npc-grid-sm text-npc-caption text-npc-amber-ink"><p className="m-0">{t('development_task.hint.timeline_error')}</p><Button variant="secondary" size="sm" className="mt-npc-gap-sm" onClick={retry}>{t('development_task.action.retry')}</Button></div> : null}{!pending && !error && events.length === 0 ? <p className="m-0 text-npc-caption text-npc-ink-soft">{t('development_task.hint.timeline_empty')}</p> : null}{events.length ? <ol className="m-0 flex list-none flex-col divide-y divide-npc-border-softer p-0">{[...events].sort((a, b) => a.sequence - b.sequence).map((event) => <li key={event.sequence} className="grid grid-cols-[auto_minmax(0,1fr)] gap-npc-gap-lg py-npc-row-y"><StatusDot tone={eventTone(event)} className="mt-[6px]" /><div className="min-w-0"><div className="text-npc-body-sm font-medium text-npc-ink">{eventLabel(event)}</div><div className="mt-npc-gap-xs flex flex-wrap gap-x-npc-gap-lg gap-y-npc-gap-xs text-npc-meta text-npc-ink-softer"><time dateTime={event.created_at}>{timestamp(event.created_at)}</time>{eventActor(event) ? <span>{eventActor(event)}</span> : null}</div></div></li>)}</ol> : null}</div></Card>
}

function ErrorNotice({ message }: { message: string }) { return <p role="alert" className="m-0 border-l-2 border-npc-red bg-npc-red-bg px-npc-grid-sm py-npc-gap-sm text-npc-caption text-npc-red-ink">{message}</p> }
function Detail({ label, value, mono = false }: { label: string; value: string | null | undefined; mono?: boolean }) { return value ? <><dt className="text-npc-ink-softer">{label}</dt><dd className={cn('m-0 break-all text-npc-ink-medium', mono && 'font-npc-mono')}>{value}</dd></> : null }
function humanState(state: string) { return t(stateCopy[state] ?? 'development_task.state.draft') }
function eventLabel(event: DevelopmentTaskEvent) { const state = eventState(event); return state ? humanState(state) : t(eventCopy[event.type] ?? 'development_task.event.updated') }
function eventTone(event: DevelopmentTaskEvent): TagTone { const value = `${event.type} ${eventState(event) ?? ''}`; if (/(failed|rejected|cancelled)/.test(value)) return 'danger'; if (/(fix_requested|waiting|ready_to_claim)/.test(value)) return 'warn'; if (/(passed|ready|completed|claimed)/.test(value)) return 'ok'; return 'neutral' }
function stateTone(state: string): TagTone {
  if (state === 'ready' || state === 'ready_to_claim' || state === 'claimable' || state === 'live') return 'ok'
  if (state === 'failed' || state === 'cancelled' || state === 'needs_action') return 'danger'
  if (/(fix|draft|needs_action)/.test(state)) return 'warn'
  return 'info'
}
function eventActor(event: DevelopmentTaskEvent) { const actor = event.payload?.executor_kind ?? event.payload?.executor; return actor === 'external' ? t('development_task.actor.external') : actor === 'internal' ? t('development_task.actor.internal') : null }
function eventState(event: DevelopmentTaskEvent) { const state = event.payload?.to ?? event.payload?.state; return typeof state === 'string' ? state : null }
function timestamp(value: string) { const date = new Date(value); return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { hour12: false }) }
function defaultExecutorId() { return typeof navigator === 'undefined' ? 'external-computer' : `external-${navigator.platform || 'computer'}`.replace(/[^A-Za-z0-9._-]/g, '-').slice(0, 128) }
function completeExecutorIdentity(provider: string, model: string): DevelopmentTaskExecutorIdentity | undefined { const executorProvider = provider.trim(); const executorModel = model.trim(); return executorProvider && executorModel ? { executor_provider: executorProvider, executor_model: executorModel } : undefined }
function hasPartialExecutorIdentity(provider: string, model: string) { return Boolean(provider.trim()) !== Boolean(model.trim()) }
function claimText(result: DevelopmentTaskClaim) { return [t('development_task.claim.copy'), '', 'NPC_TASK_PACKET_JSON', JSON.stringify(result.task_packet, null, 2), '', 'REPOGATE_CREDENTIALS_JSON', JSON.stringify(result.claim, null, 2)].join('\n') }
